import {
  type ApprovalAuditReceipt,
  type ApprovalDocument,
  type ApprovalHistoryEvent,
  type ApprovalHubQuery,
  type ApprovalIntakeDraft,
  type ApprovalIntakeDraftCursor,
  type ApprovalIntakeDraftPage,
  type ApprovalLineDesignerSnapshot,
  type ApprovalLineSelection,
  type ApprovalLineTemplateCommand,
  type ApprovalRequest,
  type ApprovalRouteExtensionReceipt,
  type ApprovalScope,
  type ApprovalSubmissionReceipt,
  type ApprovalWithdrawalReceipt,
  type ApprovalWorkspacePage,
  type ApprovalWorkspaceRow,
  type AuthoritativeApprovalPorts,
  type ImmutableSourceRevisionRef,
} from './model';
import { tbmRecordSchema } from '@kop/tbm';
import { educationRecordSchema } from '@kop/safety-education';
import type { PrintViewModel } from '@kop/print';
import type {
  ApprovalFirebaseConfig,
  ApprovalFirebaseRepository,
} from '@kop/approval-firebase-adapter';
import { approvalIntakeSchema } from '@kop/contracts';
import { featureAccessProjectionStatus } from '../../feature-access-projection';
import { parseRemediationReport } from './remediation-report';

type Environment = Readonly<Record<string, string | boolean | undefined>>;
type Repository = ApprovalFirebaseRepository;

const record = (value: unknown): Readonly<Record<string, unknown>> =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : {};
const array = (value: unknown) => (Array.isArray(value) ? value : []);

function normalizeScope(value: unknown): ApprovalScope {
  const item = record(value);
  if (item.kind === 'contractor')
    return {
      kind: 'contractor',
      tenantId: String(item.tenantId),
      siteId: String(item.siteId),
      contractorId: String(item.contractorId),
    };
  if (item.kind === 'tenant') return { kind: 'tenant', tenantId: String(item.tenantId) };
  return { kind: 'site', tenantId: String(item.tenantId), siteId: String(item.siteId) };
}

function source(value: unknown): ImmutableSourceRevisionRef {
  const item = record(value);
  return {
    sourceDomain: String(item.sourceDomain),
    documentKind: String(item.documentKind),
    documentId: String(item.documentId),
    revisionId: String(item.revisionId),
    revisionNumber: Number(item.revisionNumber),
    contentHash: String(item.contentHash),
    algorithm: 'sha256',
    scope: normalizeScope(item.scope),
  };
}

export function parseTbmApprovalReport(value: unknown, expected: ImmutableSourceRevisionRef) {
  const report = record(value);
  const tbm = tbmRecordSchema.parse(report.record);
  if (expected.sourceDomain !== 'tbm' || expected.documentKind !== 'daily-tbm' ||
    tbm.recordId !== expected.documentId || tbm.version !== expected.revisionNumber ||
    tbm.contentHash !== expected.contentHash || report.contentHash !== expected.contentHash ||
    report.immutable !== true)
    throw new Error('TBM_REPORT_BINDING_MISMATCH');
  return { record: tbm, contentHash: String(report.contentHash), recordHash: String(report.recordHash) };
}

export function parseEducationApprovalReport(value: unknown, expected: ImmutableSourceRevisionRef) {
  const bundle = record(value);
  const sourceRef = record(bundle.source);
  const education = educationRecordSchema.parse(bundle.record);
  const report = bundle.report as PrintViewModel | undefined;
  const media = array(bundle.media).map(item => {
    const entry = record(item);
    return { mediaManifestId: String(entry.mediaManifestId), evidenceId: String(entry.evidenceId),
      contentHash: String(entry.contentHash), caption: String(entry.caption) };
  });
  const scopeSiteId = expected.scope.kind === 'tenant' ? null : expected.scope.siteId;
  const photoKeys = new Set((education.execution?.photos ?? [])
    .map(photo => `${photo.mediaManifestId}:${photo.evidenceId}:${photo.contentHash}:${photo.caption}`));
  if (expected.sourceDomain !== 'safety-education' || expected.documentKind !== 'education-journal' ||
    education.educationId !== expected.documentId || education.version !== expected.revisionNumber ||
    sourceRef.documentId !== expected.documentId || sourceRef.revisionId !== expected.revisionId ||
    Number(sourceRef.revisionNumber) !== expected.revisionNumber || sourceRef.contentHash !== expected.contentHash ||
    sourceRef.siteId !== scopeSiteId || bundle.immutable !== true ||
    (report != null && (report.identity?.documentId !== expected.documentId || report.identity?.revisionId !== education.documentVersionId)) ||
    media.length !== photoKeys.size || media.some(item => !photoKeys.has(
      `${item.mediaManifestId}:${item.evidenceId}:${item.contentHash}:${item.caption}`)))
    throw new Error('EDUCATION_REPORT_BINDING_MISMATCH');
  return { record: education, report, media };
}

function request(value: unknown): ApprovalRequest {
  const item = record(value);
  return {
    requestId: String(item.requestId),
    round: Number(item.round),
    previousRequestId: item.previousRequestId == null ? null : String(item.previousRequestId),
    source: source(item.source),
    eventKind: String(item.eventKind),
    titleSnapshot: String(item.titleSnapshot),
    ...(typeof item.inspectionReportBindingId === 'string' ? { inspectionReportBindingId: item.inspectionReportBindingId } : {}),
    requestedByIdentityId: String(item.requestedByIdentityId),
    requestedByName: String(item.requestedByName),
    requestedAt: String(item.requestedAt),
    dueAt: String(item.dueAt),
    status: String(item.status) as ApprovalRequest['status'],
    activeSequence: item.activeSequence == null ? null : Number(item.activeSequence),
    line: array(item.line).map((entry) => {
      const member = record(entry);
      return {
        lineMemberId: String(member.lineMemberId),
        sequence: Number(member.sequence),
        role: String(member.role) as 'requester' | 'reviewer' | 'approver',
        identityId: String(member.identityId),
        displayName: String(member.displayName),
        dutyLabel: String(member.dutyLabel),
      };
    }),
    policyId: String(item.policyId),
    policyVersion: Number(item.policyVersion),
  };
}

// The server authorizes the requested viewing scope. A site inbox also contains
// that site's contractor documents; object serialization is not scope equality.
const containsDocumentScope = (viewer: ApprovalScope, document: ApprovalScope) => {
  if (viewer.tenantId !== document.tenantId) return false;
  if (viewer.kind === 'tenant') return true;
  if (document.kind === 'tenant' || viewer.siteId !== document.siteId) return false;
  return viewer.kind === 'site' ||
    (document.kind === 'contractor' && viewer.contractorId === document.contractorId);
};
const sameSource = (left: unknown, right: ImmutableSourceRevisionRef) =>
  JSON.stringify(source(left)) === JSON.stringify(right);

function requireReceipt(
  value: Readonly<Record<string, unknown>>,
  expected: Readonly<{
    commandId: string;
    idempotencyKey: string;
    requestId: string;
    source: ImmutableSourceRevisionRef;
  }>,
) {
  if (
    value.immutable !== true ||
    value.commandId !== expected.commandId ||
    value.idempotencyKey !== expected.idempotencyKey ||
    value.requestId !== expected.requestId ||
    !sameSource(value.source, expected.source)
  )
    throw new Error('APPROVAL_SERVER_RECEIPT_MISMATCH');
}

const serverFilter: Readonly<Record<ApprovalHubQuery['filter'], string>> = {
  'my-pending': 'pending',
  'requested-by-me': 'authored',
  completed: 'completed',
  returned: 'returned',
  overdue: 'overdue',
  all: 'all',
};

function workspaceRow(value: unknown): ApprovalWorkspaceRow {
  const item = record(value);
  const requestedBy = record(item.requestedBy);
  const activeStep = item.activeStep == null ? null : record(item.activeStep);
  return {
    requestId: String(item.requestId),
    version: Number(item.version),
    status: String(item.status) as ApprovalWorkspaceRow['status'],
    title: String(item.title),
    sourceDomain: String(item.sourceDomain),
    documentKind: String(item.documentKind),
    revisionNumber: Number(item.revisionNumber),
    requestedBy: {
      identityId: String(requestedBy.identityId),
      displayName: String(requestedBy.displayName),
    },
    requestedAt: String(item.requestedAt),
    dueAt: String(item.dueAt),
    activeStep:
      activeStep == null
        ? null
        : {
            sequence: Number(activeStep.sequence),
            role: String(activeStep.role) as 'reviewer' | 'approver',
            displayName: String(activeStep.displayName),
            delegated: activeStep.delegated === true,
            delegationId: activeStep.delegationId == null ? null : String(activeStep.delegationId),
          },
    actions: array(item.actions).map(String) as ApprovalWorkspaceRow['actions'],
  };
}

function history(response: Readonly<Record<string, unknown>>, value: ApprovalRequest) {
  const events: ApprovalHistoryEvent[] = [];
  const submission = record(response.submissionReceipt);
  events.push({
    eventId: String(submission.receiptId),
    kind: 'submitted',
    actorName: value.requestedByName,
    reason: null,
    recordedAt: String(submission.recordedAt),
    resultingStatus: 'requested',
    serverSequence: Number(submission.serverSequence),
  });
  for (const entry of array(response.decisionReceipts)) {
    const receipt = record(entry);
    const actorId = String(receipt.actorIdentityId);
    events.push({
      eventId: String(receipt.receiptId),
      kind: receipt.action === 'return' ? 'returned' : 'approved-stage',
      actorName: value.line.find((member) => member.identityId === actorId)?.displayName ?? actorId,
      reason: receipt.reason == null ? null : String(receipt.reason),
      recordedAt: String(receipt.recordedAt),
      resultingStatus: String(receipt.resultingStatus) as ApprovalRequest['status'],
      serverSequence: Number(receipt.serverSequence),
    });
  }
  const withdrawal = response.withdrawalReceipt == null ? null : record(response.withdrawalReceipt);
  if (withdrawal)
    events.push({
      eventId: String(withdrawal.receiptId),
      kind: 'withdrawn',
      actorName: value.requestedByName,
      reason: String(withdrawal.reason),
      recordedAt: String(withdrawal.recordedAt),
      resultingStatus: 'withdrawn',
      serverSequence: Number(withdrawal.serverSequence),
    });
  return events.sort((left, right) => left.serverSequence - right.serverSequence);
}

function endpoint(
  hostValue: string | boolean | undefined,
  portValue: string | boolean | undefined,
) {
  const host = String(hostValue ?? '127.0.0.1')
    .replace(/^https?:\/\//u, '')
    .split(':')[0]!;
  const port = Number(portValue ?? 15001);
  if (!['127.0.0.1', 'localhost', '::1'].includes(host) || !Number.isInteger(port))
    throw new Error('APPROVAL_EMULATOR_LOOPBACK_ONLY');
  return { host, port };
}

export class LazyApprovalAuthority implements AuthoritativeApprovalPorts {
  private readonly repository: Promise<Repository>;

  constructor(config: ApprovalFirebaseConfig, repository?: Promise<Repository>) {
    this.repository =
      repository ??
      import('@kop/approval-firebase-adapter').then((module) =>
        module.createApprovalFirebaseAdapter(config),
      );
  }

  async loadInbox(
    query: ApprovalHubQuery,
    cursor: { readonly requestedAt: string; readonly requestId: string } | null = null,
  ) {
    const response = record(
      await (
        await this.repository
      ).loadWorkspace({
        scope: query.scope,
        filter: serverFilter[query.filter],
        pageSize: 30,
        cursor,
      }),
    );
    return {
      rows: array(response.rows).map(workspaceRow),
      nextCursor:
        response.nextCursor == null
          ? null
          : {
              requestedAt: String(record(response.nextCursor).requestedAt),
              requestId: String(record(response.nextCursor).requestId),
            },
      completeness: response.completeness === 'more' ? 'more' : 'complete',
    } satisfies ApprovalWorkspacePage;
  }

  async loadRequest(requestId: string, scope: ApprovalScope) {
    const response = record(await (await this.repository).loadDocument({ requestId, scope, reviewFormat: 'record-v1' }));
    const raw = record(response.request);
    const value = request(raw);
    if (!containsDocumentScope(scope, value.source.scope)) return null;
    return {
      request: value,
      version: Number(raw.version),
      allowedActions: array(raw.allowedActions).map(String) as ApprovalDocument['allowedActions'],
      history: history(response, value),
      resubmissionSource:
        response.resubmissionSource == null ? null : source(response.resubmissionSource),
      resubmissionIntake:
        response.resubmissionIntake == null
          ? null
          : approvalIntakeSchema.parse(response.resubmissionIntake),
      lineMode: raw.lineMode === 'server-resolved' ? 'server-resolved' : 'requester',
      delegationId: response.delegationId == null ? null : String(response.delegationId),
      effectiveRouteRevision: Number(response.effectiveRouteRevision),
      canExtendRoute: response.canExtendRoute === true && !(raw.lineMode === 'server-resolved' &&
        ['work-control', 'inspection', 'risk-assessment'].includes(value.source.sourceDomain)),
      requireSignatureUse: record(response.policy).requireSignatureUse === true,
      presentation:
        response.presentation == null
          ? null
          : {
              title: String(record(response.presentation).title),
              subtitle:
                record(response.presentation).subtitle == null
                  ? null
                  : String(record(response.presentation).subtitle),
              sections: array(record(response.presentation).sections).map(record),
              canonicalSnapshot: record(response.presentation).canonicalSnapshot,
              canonicalSnapshotHash: typeof record(response.presentation).canonicalSnapshotHash === 'string'
                ? String(record(response.presentation).canonicalSnapshotHash) : undefined,
            },
      evidenceReceipts: array(response.evidenceReceipts).map(record),
      remediationReport: value.source.documentKind === 'inspection-remediation'
        ? parseRemediationReport(response.remediationReport, value.source) : null,
      tbmReport: value.source.sourceDomain === 'tbm' && value.source.documentKind === 'daily-tbm'
        ? parseTbmApprovalReport(response.tbmReport, value.source)
        : null,
      educationReport: value.source.sourceDomain === 'safety-education' && value.source.documentKind === 'education-journal'
        ? parseEducationApprovalReport(response.educationReport, value.source)
        : null,
    } satisfies ApprovalDocument;
  }

  async submitDecision(
    document: ApprovalDocument,
    action: 'approve' | 'return',
    reason: string | null,
    inspectionReportReceiptId?: string,
  ): Promise<ApprovalAuditReceipt> {
    const commandId = crypto.randomUUID();
    const idempotencyKey = crypto.randomUUID();
    let signatureUseReceiptId: string | null = null;
    if (document.requireSignatureUse) {
      const myInfo = record(await (await this.repository).loadMyInfo());
      const signature = [...array(myInfo.signatureRevisions)]
        .map(record)
        .reverse()
        .find((candidate) => candidate.status === 'active');
      if (!signature) throw new Error('APPROVAL_ACTIVE_SIGNATURE_REQUIRED');
      signatureUseReceiptId = crypto.randomUUID();
      const bound = record(
        await (
          await this.repository
        ).bindSignatureUse({
          receiptId: signatureUseReceiptId,
          signatureId: String(signature.signatureId),
          signatureVersion: Number(signature.version),
          targetDocumentId: document.request.requestId,
          targetRevision: document.version,
          targetContentSha256: document.request.source.contentHash,
          explicitConfirmation: true,
          delegationId: document.delegationId,
        }),
      );
      if (
        bound.receiptId !== signatureUseReceiptId ||
        bound.targetDocumentId !== document.request.requestId ||
        Number(bound.targetRevision) !== document.version ||
        bound.targetContentSha256 !== document.request.source.contentHash ||
        bound.effective !== true
      )
        throw new Error('APPROVAL_SIGNATURE_RECEIPT_MISMATCH');
    }
    const response = record(
      await (
        await this.repository
      ).decide({
        commandId,
        idempotencyKey,
        requestId: document.request.requestId,
        action,
        reason,
        expectedVersion: document.version,
        expectedSource: document.request.source,
        delegationId: document.delegationId,
        signatureUseReceiptId,
        ...(inspectionReportReceiptId ? { inspectionReportReceiptId } : {}),
      }),
    );
    const receipt = record(response.receipt);
    if (
      receipt.immutable !== true ||
      receipt.commandId !== commandId ||
      receipt.idempotencyKey !== idempotencyKey ||
      receipt.requestId !== document.request.requestId ||
      receipt.sourceRevisionId !== document.request.source.revisionId ||
      receipt.sourceContentHash !== document.request.source.contentHash ||
      receipt.previousStatus !== document.request.status ||
      receipt.action !== action
    )
      throw new Error('APPROVAL_SERVER_RECEIPT_MISMATCH');
    return receipt as ApprovalAuditReceipt;
  }


  async withdraw(document: ApprovalDocument, reason: string) {
    const commandId = crypto.randomUUID();
    const idempotencyKey = crypto.randomUUID();
    const response = record(
      await (
        await this.repository
      ).withdraw({
        commandId,
        idempotencyKey,
        requestId: document.request.requestId,
        reason,
        expectedVersion: document.version,
        expectedSource: document.request.source,
      }),
    );
    const receipt = record(response.receipt);
    requireReceipt(receipt, {
      commandId,
      idempotencyKey,
      requestId: document.request.requestId,
      source: document.request.source,
    });
    if (receipt.status !== 'withdrawn' || receipt.reason !== reason)
      throw new Error('APPROVAL_SERVER_RECEIPT_MISMATCH');
    return receipt as ApprovalWithdrawalReceipt;
  }

  async extendRouteAfterSafetyReview(
    document: ApprovalDocument,
    reason: string,
    reviewerIdentityIds: readonly string[],
  ): Promise<ApprovalRouteExtensionReceipt> {
    if (!document.canExtendRoute) throw new Error('APPROVAL_ROUTE_EXTENSION_FORBIDDEN');
    const commandId = crypto.randomUUID();
    const idempotencyKey = crypto.randomUUID();
    const response = record(
      await (
        await this.repository
      ).extendAfterSafetyReview({
        commandId,
        idempotencyKey,
        requestId: document.request.requestId,
        expectedStateVersion: document.version,
        expectedRouteRevision: document.effectiveRouteRevision,
        reason,
        reviewerIdentityIds,
      }),
    );
    const receipt = record(response.receipt);
    if (
      receipt.immutable !== true ||
      receipt.receiptId !== commandId ||
      receipt.requestId !== document.request.requestId ||
      receipt.previousRouteRevisionId == null ||
      receipt.revision !== document.effectiveRouteRevision + 1 ||
      receipt.sourceRevisionId !== document.request.source.revisionId ||
      receipt.sourceContentHash !== document.request.source.contentHash ||
      !Array.isArray(receipt.addedReviewerIdentityIds) ||
      JSON.stringify(receipt.addedReviewerIdentityIds) !== JSON.stringify(reviewerIdentityIds)
    )
      throw new Error('APPROVAL_ROUTE_EXTENSION_RECEIPT_MISMATCH');
    return receipt as ApprovalRouteExtensionReceipt;
  }

  async resubmit(document: ApprovalDocument, lineSelection: ApprovalLineSelection) {
    const intake = document.resubmissionIntake;
    const commandId = intake?.intakeId ?? crypto.randomUUID();
    const idempotencyKey = intake?.intakeId ?? crypto.randomUUID();
    const expectedSource = document.resubmissionSource ?? document.request.source;
    if (
      intake &&
      !sameSource(
        {
          ...intake.source,
          revisionId: intake.source.documentVersionId,
          revisionNumber: intake.source.documentVersion,
          algorithm: 'sha256',
        },
        expectedSource,
      )
    )
      throw new Error('APPROVAL_RESUBMISSION_SOURCE_MISMATCH');
    const response = record(
      await (
        await this.repository
      ).submitSource({
        commandId,
        idempotencyKey,
        eventKind: document.request.eventKind,
        titleSnapshot: intake?.presentation.title ?? document.request.titleSnapshot,
        source: expectedSource,
        previousRequestId: document.request.requestId,
        lineSelection,
        ...(intake
          ? {
              intakeSnapshot: {
                intakeId: intake.intakeId,
                integrityReceiptId: intake.source.integrityReceiptId,
                trigger: intake.trigger,
                presentation: intake.presentation,
                completion: intake.completion,
              },
            }
          : {}),
      }),
    );
    const receipt = record(response.receipt);
    requireReceipt(receipt, {
      commandId,
      idempotencyKey,
      requestId: commandId,
      source: expectedSource,
    });
    if (receipt.status !== 'requested') throw new Error('APPROVAL_SERVER_RECEIPT_MISMATCH');
    return receipt as ApprovalSubmissionReceipt;
  }

  async loadIntakeDrafts(
    scope: ApprovalScope,
    cursor: ApprovalIntakeDraftCursor | null = null,
  ): Promise<ApprovalIntakeDraftPage> {
    const response = record(await (await this.repository).loadIntakeDrafts({ scope, cursor }));
    const next = response.nextCursor == null ? null : record(response.nextCursor);
    if (
      next &&
      (typeof next.recordedAt !== 'string' ||
        !Number.isFinite(Date.parse(next.recordedAt)) ||
        typeof next.documentId !== 'string' ||
        !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(next.documentId))
    )
      throw new Error('APPROVAL_DRAFT_CURSOR_INVALID');
    const drafts = array(response.drafts).map((value) => {
      const draft = record(value);
      return {
        intakeId: String(draft.intakeId),
        title: String(draft.title),
        sourceDomain: String(draft.sourceDomain),
        documentKind: String(draft.documentKind),
        recordedAt: String(draft.recordedAt),
        scope: normalizeScope(draft.scope),
      };
    });
    return {
      drafts,
      nextCursor: next
        ? { recordedAt: String(next.recordedAt), documentId: String(next.documentId) }
        : null,
    };
  }

  async resumeIntakeDraft(draft: ApprovalIntakeDraft, lineSelection: ApprovalLineSelection) {
    const idempotencyKey = draft.intakeId;
    const response = record(
      await (
        await this.repository
      ).resumeIntakeDraft({
        scope: draft.scope,
        intakeId: draft.intakeId,
        idempotencyKey,
        lineSelection: lineSelection.map((member) => ({
          identityId: member.identityId,
          organizationGroupId: member.groupId,
        })),
      }),
    );
    if (
      response.immutable !== true ||
      response.intakeId !== draft.intakeId ||
      response.idempotencyKey !== idempotencyKey ||
      response.status !== 'requested'
    )
      throw new Error('APPROVAL_DRAFT_RECEIPT_MISMATCH');
    return response as unknown as ApprovalSubmissionReceipt;
  }

  async loadLineDesigner(scope: ApprovalScope): Promise<ApprovalLineDesignerSnapshot> {
    const response = record(await (await this.repository).loadLineDesigner({ scope }));
    return response as ApprovalLineDesignerSnapshot;
  }

  async executeLineTemplate(command: ApprovalLineTemplateCommand): Promise<void> {
    const response = record(await (await this.repository).executeLineTemplate(command));
    if (response.status !== 'succeeded') throw new Error('APPROVAL_LINE_TEMPLATE_NOT_SAVED');
  }
}

export function createApprovalAuthority(
  environment: Environment = import.meta.env,
  runtime: Readonly<{ development: boolean; hostname: string; pathname: string }> = {
    development: import.meta.env.DEV,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
  },
): AuthoritativeApprovalPorts | null {
  const selected = environment.VITE_APPROVAL_DATA_ADAPTER;
  const projectId = String(environment.VITE_FIREBASE_PROJECT_ID ?? '');
  if (selected === 'review') {
    if (
      environment.VITE_AUTH_ADAPTER !== 'local' ||
      environment.VITE_REVIEW_MODE !== 'true' ||
      environment.VITE_LOCAL_PREVIEW !== 'true' ||
      !projectId.startsWith('demo-') ||
      !runtime.development ||
      !['127.0.0.1', 'localhost', '::1'].includes(runtime.hostname) ||
      runtime.pathname !== '/approvals'
    )
      throw new Error('APPROVAL_REVIEW_MODE_LOCAL_DEMO_ONLY');
    return new LazyApprovalAuthority(
      {
        mode: 'firebase-emulator',
        projectId,
        functions: { host: '127.0.0.1', port: 1 },
      },
      import('./review-approval-repository').then((module) =>
        module.createReviewApprovalRepository(),
      ),
    );
  }
  if (selected === 'firebase-emulator') {
    if (environment.VITE_AUTH_ADAPTER !== 'firebase-emulator' || !projectId.startsWith('demo-'))
      throw new Error('APPROVAL_TRUSTED_EMULATOR_SESSION_REQUIRED');
    return new LazyApprovalAuthority({
      mode: 'firebase-emulator',
      projectId,
      functions: endpoint(
        environment.VITE_FUNCTIONS_EMULATOR_HOST,
        environment.VITE_FUNCTIONS_EMULATOR_PORT,
      ),
    });
  }
  if (selected === 'firebase') {
    const required = [
      projectId,
      environment.VITE_FIREBASE_API_KEY,
      environment.VITE_FIREBASE_APP_ID,
      environment.VITE_FIREBASE_AUTH_DOMAIN,
      environment.VITE_FIREBASE_APPCHECK_SITE_KEY,
    ];
    if (
      environment.VITE_AUTH_ADAPTER !== 'firebase' ||
      environment.VITE_APPROVAL_CORPORATE_RUNTIME_VERIFIED !== 'true' ||
      projectId.startsWith('demo-') ||
      required.some((item) => String(item ?? '').trim() === '')
    )
      return null;
    return new LazyApprovalAuthority({
      mode: 'firebase',
      projectId,
      corporateRuntimeVerified: true,
    });
  }
  return null;
}

// Constructed on first use, never at module evaluation. See getInspectionDataAdapter.
let approvalAuthorityHolder: { value: AuthoritativeApprovalPorts | null } | null = null;

/** The process-wide approval authority, or null when no authority is configured. */
export function getApprovalAuthority(): AuthoritativeApprovalPorts | null {
  approvalAuthorityHolder ??= { value: createApprovalAuthority() };
  return approvalAuthorityHolder.value;
}

export function canOpenApprovalHub(role: string, capabilities: readonly string[], featureAccess?: unknown, developerReadOnly = false) {
  void role;
  const feature = featureAccessProjectionStatus(featureAccess);
  if (feature.kind === 'malformed') return false;
  if (feature.kind === 'enforced') return feature.features.has('electronic-approval');
  void developerReadOnly;
  return capabilities.includes('approval:read');
}
